// Contracts — the STAFF side of a clip's life: the work queues, the status
// moves, the verdicts that turn hours into money.
//
// Why the moves are a whitelist rather than "set any status": the lifecycle is
// also the money trail. `accepted` posts a ledger entry; `delivered` is a claim
// that the material physically left for the client. Letting a screen assign an
// arbitrary status would make both silently wrong, and the error would surface
// as a payout dispute weeks later.
import { z } from 'zod';
import {
  VistaCaptureSourceSchema,
  VistaCaptureStatusSchema,
  VistaOrientationSchema,
  VistaTsSchema,
} from './enums';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// GET /api/vista/captures — the queue view. Filters are all optional; the
// scope (which team's clips you may see at all) is enforced server-side.
//
// `from`/`to`/`q` were added for the staff "Просмотр" workflow (kids parity,
// 2026-08-12): QA on delivered/pending clips happens before the client's
// verdict, and a 300-row tab is too big to eyeball without narrowing it down.
// Both are additive — a caller that never sends them gets exactly the old
// behavior (kept for the currently-deployed web bundle during rollout).
export const VistaCapturesQuerySchema = z.object({
  status: VistaCaptureStatusSchema.optional(),
  contributorId: z.string().trim().max(64).optional(),
  // Window over the same field the list is ordered by
  // (COALESCE(uploaded_at, created_at)) — days inclusive, UTC.
  from: z.string().regex(DATE_RE).optional(),
  to: z.string().regex(DATE_RE).optional(),
  // Free-text search: contributor name, email, task topic, or clip id
  // (substring match on any of the four).
  q: z.string().trim().max(80).optional(),
  limit: z.number().int().min(1).max(500).optional(),
});
export type VistaCapturesQuery = z.infer<typeof VistaCapturesQuerySchema>;

// One entry in the "по контрибьютору" filter dropdown — scoped by the same
// pool + status as the list itself, but NOT by contributorId/from/to/q, same
// reasoning as kids' `mods`: if the option list came from the filtered rows,
// picking one contributor would collapse the dropdown down to just them, with
// no way back to "all" short of reloading.
export const VistaCaptureContributorOptionSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  n: z.number().int().nonnegative(),
});
export type VistaCaptureContributorOption = z.infer<typeof VistaCaptureContributorOptionSchema>;

export const VistaQueueRowSchema = z.object({
  id: z.string(),
  contributorId: z.string().nullable(),
  contributorEmail: z.string(),
  contributorName: z.string().nullable(),
  ownerName: z.string().nullable(),
  taskSlug: z.string().nullable(),
  durationSeconds: z.number().int(),
  status: VistaCaptureStatusSchema,
  orientation: VistaOrientationSchema.nullable(),
  integrityRatio: z.number().nullable(),
  // Пустая целостность у 'upload' — норма, а не сбой гейта. Без источника
  // рядом штаб читал бы прочерк как «проверка не отработала».
  source: VistaCaptureSourceSchema,
  bytes: z.number().int(),
  // Present once the dubbed master exists — the UI shows which file it is
  // offering, since the raw and the voiced clip are different deliverables.
  hasVoiced: z.boolean(),
  // Состояние оригинального звука: он приходит отдельной дорожкой и сводится с
  // видео на мини. Штабу это видно как факт — «звук ждёт сведения» объясняет
  // немой клип в вивере лучше, чем тишина без объяснений.
  //   none    — звуковой дорожки нет (клип снят до появления записи звука
  //             или загружен готовым файлом);
  //   pending — дорожка есть, сведение ещё не прошло;
  //   mixed   — сведено, файл отдаётся со звуком.
  audio: z.enum(['none', 'pending', 'mixed']),
  rejectReason: z.string().nullable(),
  // Диагностика брака (JSON от рекордера: кадры/дропы/стена-часы). Есть только
  // у карантина — по ней штаб видит, ЧТО сломалось, не скачивая файл.
  diag: z.string().nullable().optional(),
  // Compact device string (F10) — clips from different phones are otherwise
  // indistinguishable in the queue. Null for clips filmed before this landed.
  device: z.string().nullable().optional(),
  recordedAt: VistaTsSchema,
  uploadedAt: VistaTsSchema,
  deliveredAt: VistaTsSchema,
  verdictAt: VistaTsSchema,
});
export type VistaQueueRow = z.infer<typeof VistaQueueRowSchema>;

export const VistaCapturesResSchema = z.object({
  rows: z.array(VistaQueueRowSchema),
  count: z.number().int(),
  // Filter dropdown data — see VistaCaptureContributorOptionSchema above.
  contributors: z.array(VistaCaptureContributorOptionSchema),
});
export type VistaCapturesRes = z.infer<typeof VistaCapturesResSchema>;

/**
 * The moves staff may make, as a closed set.
 *
 * `deliver` and `pending` are separate on purpose: handing a batch over and
 * waiting for a verdict are different facts, and merging them would make "what
 * is the client still sitting on?" unanswerable.
 *
 * `accept` / `reject` are the CLIENT's verdict, recorded by us because they have
 * no API — reconciliation is manual, which is exactly why accrual has to be
 * replay-safe.
 */
export const VistaCaptureMoveSchema = z.enum([
  'queue_voiceover', // filmed → needs_voiceover (usually automatic on finish)
  'deliver', // voiced → delivered
  'pending', // delivered → pending (sent, awaiting verdict)
  'accept', // pending/delivered → accepted  (posts money)
  'reject', // any → rejected, with a reason
  'requeue', // rejected/voiced → needs_voiceover (re-dub, e.g. bad narration)
]);
export type VistaCaptureMove = z.infer<typeof VistaCaptureMoveSchema>;

/**
 * Reject reasons, taken from the client's own refusal list so the wording a
 * contributor sees matches the wording we are judged by. `other` carries free
 * text — a reason nobody can name is worse than an untidy enum.
 */
export const VistaRejectReasonSchema = z.enum([
  'integrity', // our own gate: the video is shorter than its timer
  'no_narration',
  'weak_narration', // talks, but not about what is on screen
  'hands_out_of_frame',
  'poor_light',
  'motion_blur',
  'repetitive',
  'staged',
  'privacy', // bystanders, documents, screens
  'duplicate',
  'off_topic',
  'other',
]);
export type VistaRejectReason = z.infer<typeof VistaRejectReasonSchema>;

// POST /api/vista/captures/move — one move applied to a batch of clips. Batched
// because that is how the work actually happens: a shipment is thirty clips, not
// one, and thirty round trips would invite a half-applied batch.
export const VistaCaptureMoveReqSchema = z.object({
  ids: z.array(z.string().trim().min(1).max(64)).min(1).max(200),
  move: VistaCaptureMoveSchema,
  reason: VistaRejectReasonSchema.optional(),
  note: z.string().trim().max(500).optional(),
});
export type VistaCaptureMoveReq = z.infer<typeof VistaCaptureMoveReqSchema>;

export const VistaCaptureMoveResSchema = z.object({
  moved: z.number().int(),
  // Ids the move did not apply to, with why. Never silently dropped: a batch
  // that half-worked must say which half.
  skipped: z.array(z.object({ id: z.string(), reason: z.string() })),
  // Money posted as a side effect of `accept`, in cents (what we now owe).
  accruedCents: z.number().int(),
  // Деньги, СНЯТЫЕ обратно, когда клиент отклонил уже принятый клип. Отдельным
  // числом, а не минусом в accruedCents: «начислили 5, отменили 3» и «начислили
  // 2» — разные события, и штаб должен видеть оба.
  reversedCents: z.number().int().optional(),
  // Clips that could not accrue because a rate is missing — the manager has to
  // fix that, and it must not be discovered at payout time.
  unratedIds: z.array(z.string()),
});
export type VistaCaptureMoveRes = z.infer<typeof VistaCaptureMoveResSchema>;


// ── приём вердиктов клиента списком ─────────────────────────────────────────
// У клиента нет API: вердикты приходят таблицей. Штаб вставляет её текстом,
// сервер разбирает и сопоставляет с клипами, человек СМОТРИТ предпросмотр — и
// только потом применяет обычным /captures/move. Разбор и применение
// намеренно разведены: список от клиента почти всегда содержит строку, которую
// мы не нашли, и узнавать об этом надо ДО того, как деньги начислены.
export const VistaVerdictsPreviewReqSchema = z.object({
  text: z.string().min(1).max(100_000),
});
export type VistaVerdictsPreviewReq = z.infer<typeof VistaVerdictsPreviewReqSchema>;

export const VistaVerdictsPreviewRowSchema = z.object({
  raw: z.string(),
  key: z.string(),
  move: z.enum(['accept', 'reject']).nullable(),
  reason: VistaRejectReasonSchema.nullable(),
  note: z.string().nullable(),
  // Что нашли по ключу. null во всех трёх — клип не найден (или не в моём пуле).
  id: z.string().nullable(),
  fileName: z.string().nullable(),
  contributorName: z.string().nullable(),
  status: VistaCaptureStatusSchema.nullable(),
  // Ключ подошёл больше чем одному клипу — применять вслепую нельзя.
  ambiguous: z.boolean(),
});
export type VistaVerdictsPreviewRow = z.infer<typeof VistaVerdictsPreviewRowSchema>;

export const VistaVerdictsPreviewResSchema = z.object({
  rows: z.array(VistaVerdictsPreviewRowSchema),
  // Сводка, чтобы не считать глазами: сколько готово к применению и сколько
  // строк требует внимания человека.
  ready: z.number().int(),
  problems: z.number().int(),
  /** Сколько строк не поместилось в разбор. Больше нуля — человек обязан это
   *  увидеть: молча усечённый список выглядит как «всё применилось». */
  truncated: z.number().int().default(0),
});
export type VistaVerdictsPreviewRes = z.infer<typeof VistaVerdictsPreviewResSchema>;


// GET /api/vista/capture/:id/index — оглавление фрагментированного клипа.
// Нужно, потому что браузер, получив такой файл ссылкой, строит оглавление
// сам: проходит все фрагменты от начала до конца и только потом показывает
// картинку (на 20 минутах это минуты ожидания). Индекс в файле есть — мы
// читаем его за браузер и отдаём готовым.
export const VistaCaptureIndexResSchema = z.object({
  /** Размер объекта — плеер по нему считает последний диапазон. */
  fileSize: z.number().int(),
  /** [0, initSize) — ftyp + moov, инициализация MediaSource. */
  initSize: z.number().int(),
  /** Строка для MediaSource.isTypeSupported / addSourceBuffer. */
  mime: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  durationMs: z.number().int(),
  fragments: z.array(
    z.object({
      offset: z.number().int(),
      size: z.number().int(),
      timeMs: z.number().int(),
    }),
  ),
});
export type VistaCaptureIndexRes = z.infer<typeof VistaCaptureIndexResSchema>;

// ── finance ─────────────────────────────────────────────────────────────────

// GET /api/vista/finance/balances — what we owe whom, right now.
export const VistaBalanceRowSchema = z.object({
  kind: z.enum(['contributor', 'staff']),
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  // Positive = we owe them. The ledger stores earnings negative and payments
  // positive, so this is the sign-flipped sum — the screen should never do that
  // flip itself and risk showing a debt as a credit.
  owedCents: z.number().int(),
  acceptedHours: z.number(),
  rateCents: z.number().int().nullable(),
});
export type VistaBalanceRow = z.infer<typeof VistaBalanceRowSchema>;

export const VistaBalancesResSchema = z.object({
  rows: z.array(VistaBalanceRowSchema),
  totalOwedCents: z.number().int(),
});
export type VistaBalancesRes = z.infer<typeof VistaBalancesResSchema>;

// POST /api/vista/finance/payout — record that money left. Manager only.
export const VistaPayoutReqSchema = z.object({
  contributorId: z.string().trim().max(64).optional(),
  userId: z.string().trim().max(64).optional(),
  amountCents: z.number().int().min(1).max(10_000_000),
  note: z.string().trim().max(500).optional(),
  // Idempotency handle from the caller: a double-tapped payout button must not
  // pay twice, and the UI knows the intent better than any server-side guess.
  refKey: z.string().trim().min(4).max(120),
});
export type VistaPayoutReq = z.infer<typeof VistaPayoutReqSchema>;

export const VistaPayoutResSchema = z.object({
  ok: z.literal(true),
  duplicate: z.boolean(),
});
export type VistaPayoutRes = z.infer<typeof VistaPayoutResSchema>;

// GET /api/vista/finance/ledger?contributorId=…|userId=…&limit=…&offset=…
export const VistaLedgerQuerySchema = z.object({
  contributorId: z.string().trim().max(64).optional(),
  userId: z.string().trim().max(64).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  offset: z.number().int().min(0).optional(),
});
export type VistaLedgerQuery = z.infer<typeof VistaLedgerQuerySchema>;

export const VistaLedgerRowSchema = z.object({
  id: z.string(),
  type: z.enum(['accrual', 'advance', 'payout', 'adjustment']),
  amountCents: z.number().int(),
  rateCents: z.number().int().nullable(),
  note: z.string().nullable(),
  createdAt: z.number().int(),
  // Who made the entry. Null for `accrual` — the system posts those on a
  // verdict, nobody "did" it — but a payout or a manual adjustment is always
  // somebody's explicit action, and the history screen has to say whose.
  createdByName: z.string().nullable(),
});

export const VistaLedgerResSchema = z.object({
  rows: z.array(VistaLedgerRowSchema),
  balanceCents: z.number().int(),
  // Count over the WHOLE history for this target, not just the returned page —
  // the modal needs it to decide whether "load more" has anything left to load.
  total: z.number().int(),
});
export type VistaLedgerRes = z.infer<typeof VistaLedgerResSchema>;

// POST /api/vista/finance/adjust — a manual correction to one balance, manager
// only. The reason is NOT optional: an adjustment nobody can explain later is
// worse than no adjustment, since it is exactly the kind of entry that gets
// disputed at payout time. Sign follows the ledger's own convention (same as
// every other row) — positive reduces what we owe, negative increases it — so
// the UI never has to invert anything to explain the effect.
export const VistaAdjustReqSchema = z.object({
  contributorId: z.string().trim().max(64).optional(),
  userId: z.string().trim().max(64).optional(),
  amountCents: z
    .number()
    .int()
    .min(-10_000_000)
    .max(10_000_000)
    .refine((v) => v !== 0, { message: 'amount_zero' }),
  reason: z.string().trim().min(3).max(500),
  // Idempotency handle from the caller, same shape as payout's refKey.
  refKey: z.string().trim().min(4).max(120),
});
export type VistaAdjustReq = z.infer<typeof VistaAdjustReqSchema>;

export const VistaAdjustResSchema = z.object({
  ok: z.literal(true),
  duplicate: z.boolean(),
});
export type VistaAdjustRes = z.infer<typeof VistaAdjustResSchema>;

// GET /api/vista/my/ledger — the contributor's OWN ledger, joined back to the
// clip an accrual came from (topic + minutes) so "what was this for" reads in
// clip terms instead of a bare dollar figure. Contributor-session only — there
// is no target parameter, so there is nothing here to misuse into reading
// someone else's money.
export const VistaMyLedgerRowSchema = z.object({
  id: z.string(),
  type: z.enum(['accrual', 'advance', 'payout', 'adjustment']),
  amountCents: z.number().int(),
  createdAt: z.number().int(),
  note: z.string().nullable(),
  // Populated only for `accrual` rows (joined via ref_key) — null for a payout
  // or an adjustment, which are not about any one clip.
  taskSlug: z.string().nullable(),
  durationSeconds: z.number().int().nullable(),
});
export type VistaMyLedgerRow = z.infer<typeof VistaMyLedgerRowSchema>;

export const VistaMyLedgerResSchema = z.object({
  rows: z.array(VistaMyLedgerRowSchema),
});
export type VistaMyLedgerRes = z.infer<typeof VistaMyLedgerResSchema>;
